import { motion } from "framer-motion";
import { Globe, ShieldCheck, Award, FileCheck, Anchor, Ship, Package, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const CARGO = "https://images.pexels.com/photos/9806482/pexels-photo-9806482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WORLD_MAP = "https://static.prod-images.emergentagent.com/jobs/82e52e3c-f998-4a3d-91a1-430c4242e074/images/6dc2c9ca1cc842001a87b5edb5f282be4c30651eb7ed1e11710ab63b538563da.png";

const regions = [
  {
    name: "Africa",
    desc: "We are a major supplier to West and East African nations, providing consistent shipments of 1121 and 1509 Basmati along with non-basmati varieties. Our deep understanding of African market preferences makes us a preferred partner.",
    markets: ["Nigeria", "Ghana", "Kenya", "Tanzania", "Senegal"],
  },
  {
    name: "Indonesia",
    desc: "Our Indonesian partners trust us for high-quality rice that meets their specific requirements. We provide customised packaging and timely deliveries to Indonesian ports.",
    markets: ["Jakarta", "Surabaya", "Semarang"],
  },
  {
    name: "Canada",
    desc: "Serving the growing South Asian community in Canada, we export premium Basmati rice that meets Canadian food safety standards. Our rice is available through major distributors.",
    markets: ["Toronto", "Vancouver", "Calgary"],
  },
  {
    name: "Europe",
    desc: "Our presence in European markets continues to grow, supplying premium Basmati and specialty rice to retailers and distributors across the continent.",
    markets: ["United Kingdom", "Germany", "Netherlands", "Belgium"],
  },
  {
    name: "Middle East",
    desc: "The Middle East is one of our strongest markets. We supply aromatic Basmati rice to importers across the Gulf region, meeting the high standards of Middle Eastern cuisine.",
    markets: ["UAE", "Saudi Arabia", "Kuwait", "Qatar", "Oman"],
  },
];

const certifications = [
  {
    icon: Award,
    name: "APEDA",
    regNo: "Reg. No: 212810",
    validity: "Valid until: 20/10/2026",
    desc: "Agricultural and Processed Food Products Export Development Authority — registered as Merchant Exporter for all APEDA scheduled products.",
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/nykcorlk_APEDA%20CERTIFICATE.pdf",
  },
  {
    icon: ShieldCheck,
    name: "FSSAI",
    regNo: "License No: 12123999000091",
    validity: "Valid until: 01/09/2026",
    desc: "Food Safety and Standards Authority of India — Central License for Trade/Retail, Merchant Exporter operations.",
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/dl56uthj_FSSAI%20CERTIFICATE%20UPTO%202026.pdf",
  },
  {
    icon: FileCheck,
    name: "ISO 9001:2015",
    regNo: "Cert. No: PSF3Q1803V25",
    validity: "Valid until: 17/06/2028",
    desc: "International Organization for Standardization — certified for processing, import, export & packaging of Basmati, Non-Basmati Rice & Wheat.",
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/eskj88y7_ISO%20Certificate.pdf",
  },
  {
    icon: Globe,
    name: "LEI",
    regNo: "LEI: 98450081ED549ABQFD32",
    validity: "Auto-renewal until: 2027",
    desc: "Legal Entity Identifier — globally recognized identification for P.S. IMPEX, registered via GLEIF database.",
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/9u6nbp5d_LEI%20PS%20IMPEX.pdf",
  },
];

const process = [
  { icon: Package, title: "Sourcing", desc: "Direct procurement from Punjab's premium paddy farms" },
  { icon: ShieldCheck, title: "Quality Check", desc: "Multi-stage sortex and laboratory testing" },
  { icon: Anchor, title: "Processing", desc: "State-of-the-art milling and packaging" },
  { icon: Ship, title: "Shipping", desc: "Containerised export via major Indian ports" },
];

export default function ExportsPage() {
  return (
    <div data-testid="exports-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${CARGO})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Worldwide Exports
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-bone leading-[0.95] mb-6">
              Global Reach.{" "}
              <span className="italic">Uncompromised Quality.</span>
            </h1>
            <p className="text-brand-bone/80 text-base sm:text-lg font-sans max-w-2xl leading-relaxed">
              As a trusted high-volume rice exporter from India, P.S.IMPEX
              delivers premium Fab Crops rice to importers and distributors
              across continents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Export Regions */}
      <section data-testid="export-regions" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Markets
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              Where We Export
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {regions.map((region, i) => (
              <motion.div
                key={region.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white p-8 lg:p-10 rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10"
              >
                <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center mb-6">
                  <Globe size={24} className="text-brand-gold" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green mb-4">
                  {region.name}
                </h3>
                <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6">
                  {region.desc}
                </p>
                <div className="flex flex-wrap gap-2">
                  {region.markets.map((m) => (
                    <span
                      key={m}
                      className="px-3 py-1 bg-brand-sand text-brand-green text-xs font-sans font-medium rounded-full"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* World Map Section */}
      <section className="py-24 lg:py-32 bg-brand-green relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${WORLD_MAP})` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone mb-6">
              Connecting India to the World
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto leading-relaxed mb-12">
              With robust logistics partnerships and established trade routes,
              we ensure seamless delivery from Indian ports to your destination.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { num: "15+", label: "Countries" },
                { num: "50+", label: "Countries" },
                { num: "50K+", label: "Tonnes/Year" },
                { num: "99%", label: "On-Time Delivery" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-3xl lg:text-4xl font-light text-brand-gold">
                    {s.num}
                  </p>
                  <p className="text-brand-bone/60 text-xs font-sans uppercase tracking-wider mt-2">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Export Process */}
      <section data-testid="export-process" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Process
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              From Farm to Port
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-brand-green flex items-center justify-center mx-auto mb-6">
                  <Icon size={28} className="text-brand-gold" />
                </div>
                <span className="text-xs uppercase tracking-wider text-brand-gold font-sans font-semibold">
                  Step {i + 1}
                </span>
                <h4 className="font-serif text-xl font-medium text-brand-green mt-2 mb-3">
                  {title}
                </h4>
                <p className="text-brand-muted text-sm font-sans leading-relaxed">
                  {desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section data-testid="certifications-section" className="py-24 lg:py-32 bg-brand-sand">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Compliance & Trust
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              Our Certifications
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map(({ icon: Icon, name, regNo, validity, desc, pdf }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-white p-8 rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10 text-center flex flex-col"
              >
                <div className="w-16 h-16 rounded-full bg-brand-gold/10 flex items-center justify-center mx-auto mb-6">
                  <Icon size={28} className="text-brand-gold" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-brand-green mb-2">
                  {name}
                </h3>
                <p className="text-brand-gold text-xs font-sans font-semibold mb-1">{regNo}</p>
                <p className="text-brand-muted text-xs font-sans mb-4">{validity}</p>
                <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6 flex-1">
                  {desc}
                </p>
                <a
                  href={pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cert-download-${name.toLowerCase().replace(/\s/g, "-")}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-green text-brand-bone text-xs font-sans font-semibold rounded-full hover:bg-brand-gold transition-colors duration-300"
                >
                  <FileCheck size={14} />
                  View Certificate
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-6">
              Looking for a Reliable Rice Supplier?
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto mb-10 leading-relaxed">
              Partner with P.S.IMPEX for premium quality, competitive pricing,
              and hassle-free exports.
            </p>
            <Link
              to="/contact"
              data-testid="export-cta-btn"
              className="inline-flex items-center gap-2 px-10 py-4 bg-brand-gold text-brand-green font-sans font-semibold text-sm rounded-full hover:bg-brand-bone transition-colors duration-300"
            >
              Request Export Quote
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
